// THE PAGE-LEVEL ONGOING PICKER — "Ongoing Workspace" (busy-my-web#421, Figma section
// 40006949-281541, frames "search ongoins 1–6": 40010904:366285 → 40010928:372591).
//
// ── WHAT THE FRAMES ASK FOR ─────────────────────────────────────────────────────────────────────
// busy-my-web#274 answers "which Ongoing?" with an INLINE chooser inside the Briefing strip — a
// search box and eight rows in a 260px column. The updated section asks the same question in the
// MIDDLE instead, as a listing of its own with the workspace's own geometry (measured on
// 40010927:370582): 788px wide, the story title "Ongoing Workspace" over two column headings —
//
//     Ongoing name  154 │ … │ Last update  82 @ x=314 │ … │ Connect
//
// …rows 36 high on a 40px pitch, which is EXACTLY the group listing's skeleton one screen earlier
// (see EmailWorkspace.vue's #355 note). That is the point of the frame: the user is still in the
// Email Workspace, still reading a list of the same shape, and the only thing that changed is what
// the list is a list OF.
//
// This module is the pure half of that: what the listing SAYS and which ongoings it shows. No
// store, no router, nothing mounted — "does typing 'job' surface Job-offer above Job trend" is
// answerable without a browser.
//
// ── WHAT IS REUSED, AND WHY ─────────────────────────────────────────────────────────────────────
// The MATCHING is `ongoingMatches` (lib/emailThreadActions.js), unchanged and not re-implemented:
// the inline chooser and this listing must never disagree about which stories a query finds, or
// about the prefix-beats-contains rule that puts "Health issues" above "The kitchen". This module
// only joins the ranked answer back to the full record, so the listing can also draw the stamp and
// the status the frame's rows carry.
//
// The COMMIT is `emailSession/addThreadsToOngoing` (busy-my-web#274) — one request for the whole
// selection, the server's counts in the receipt. A picker with a request of its own would give the
// story a different kind of step depending on which surface the user was standing on.

import { ongoingMatches } from '@/lib/emailThreadActions'
import { lastUpdate } from '@/lib/emailSession'
import { statusMeta } from '@/lib/dueStatus'
import { originMeta } from '@/lib/ongoingOrigin'

/**
 * Verbatim design strings, in the frames' own words.
 *
 * Two of them are the frame's words with its spelling corrected, which is the same call
 * `createNotice` already recorded for "Ongoing created,My Health Issue": the source file's typo is
 * a typo, not a second name.
 *   • the search field reads "Search more throgh ongoings..." on 40010939:374052 and "Search for an
 *     Ongoing to add to..." on 40010904:366285 — the second is used, being the one that is spelled;
 *   • the success line reads "conneted" on 40010928:372591 — spelled "connected" here.
 */
export const PICKER_TEXT = {
  title: 'Ongoing Workspace',
  nameColumn: 'Ongoing name',
  lastUpdateColumn: 'Last update',
  connect: 'Connect',
  searchPlaceholder: 'Search for an Ongoing to add to...',
  // ── The 2026-09-17 redesign (busy-my-web#523, frame 40010646:341095) ──
  // Fatemeh: the possible matches are no longer cards in the box's suggestions area — the MIDDLE
  // becomes this listing, and it is headed with the question it answers. The heading is the frame's
  // own sentence case ("Possible ongoing matches", a lower-case o), which is why it is not
  // ongoingMatchesPanel's `PANEL_TEXT.title`.
  matchesTitle: 'Possible ongoing matches',
  // …and the line under it when nothing cleared the cut-off (busy-my-web#526, Saffet): the heading
  // still stands, because the listing IS the answer to "which Ongoing?", but the user is told
  // plainly that the rows below are their own Ongoings and not a suggestion about this mail.
  noConnection: 'No connection found — search for an Ongoing to add to.',
  // Nothing to show, for the two different reasons there are.
  empty: 'No ongoing matches that.',
  none: 'You have no ongoings yet.',
  back: 'Back',
  // ── The matches TABLE and the search journey (busy-my-web#535, Figma 40011882:372321 and
  // "search ongoings 1–6") ──
  // The matches are a four-column table now — what the Ongoing is, WHY it may belong, when it last
  // moved and how sure the ranking is — and the composer's typed text is a SEARCH that is pressed,
  // not a live filter: the frames draw the table unchanged under a typed query (search 2) and
  // replace it only once "Search" has run (search 4, "Job opportunities result").
  matchNameColumn: 'Name',
  reasonColumn: 'Possible connection',
  confidenceColumn: 'Confidence',
  view: 'View',
  notRelated: 'Not related',
  search: 'Search',
  loading: 'Looking for matches…',
  // The composer's prompt once a search has answered (search 4): the next query refines it.
  continuePlaceholder: 'Continue search ongoings...',
}

/** "Job opportunities result" — the middle's heading over a search's answer (search 4). */
export function resultTitle(query) {
  const q = String(query || '').trim()
  return q ? `${q} result` : PICKER_TEXT.matchesTitle
}

/**
 * How many ranked matches the table asks for and draws. Frame 40010646:341095 drew three radios in
 * the box; the table that replaced them (40011882:372321) draws six rows.
 */
export const MATCH_TABLE_TAKE = 6

// The confidence chip's three faces, measured on 40011882:372321 (px8 py3 r6, 12/16).
const CHIP = {
  Strong: 'bg-[#3741f3] text-white',
  Likely: 'bg-[#f4f9ff] text-[#3741f3]',
  Possible: 'bg-[#edeeef] text-[#4b5563]',
}

/**
 * The chip a ranked match wears: the server's own label ("Strong match" → "Strong"), the `strong`
 * flag when no label came with it, and "Possible" for anything else the ranking let through.
 */
export function confidenceChip(match) {
  const word = String(match?.label || '').trim().split(/\s+/)[0]
  const label = CHIP[word] ? word : (match?.strong ? 'Strong' : 'Possible')
  return { label, classes: CHIP[label] }
}

/** The "Possible connection" cell: the ranking's own first reason, or nothing. */
export function matchReason(match) {
  const reasons = (match?.reasons || []).map(r => String(r || '').trim()).filter(Boolean)
  return reasons[0] || ''
}

/**
 * The matches TABLE's rows (40011882:372321): the ranked candidates in the server's order, each
 * joined to the user's own record for its glyph and stamp. A candidate the store holds no record
 * for is still drawn — from its own title, with no stamp — because the ranking naming it is a
 * reason to show it.
 */
export function matchTableRows({ matches = [], ongoings = [], now = new Date() } = {}) {
  const byId = new Map((ongoings || []).filter(o => o && o.id).map(o => [String(o.id), o]))
  const seen = new Set()
  const rows = []
  for (const m of matches || []) {
    const id = String(m?.id || '')
    if (!id || seen.has(id)) continue
    seen.add(id)
    const title = String(m.title || byId.get(id)?.title || '').trim() || 'Ongoing'
    rows.push({
      ...pickerRow(id, title, byId.get(id) || {}, now),
      match: true,
      reason: matchReason(m),
      chip: confidenceChip(m),
    })
  }
  return rows
}

/**
 * A SEARCH's answer as rows (search 4): the server's ranking (`GET v1/ongoing/search`, read with
 * lib/ongoingMatchesPanel's `readExisting`), joined to the records the same way. No reason and no
 * chip — the frame's result table is "Ongoing name | Last update" and the two links.
 */
export function searchResultRows({ results = [], ongoings = [], now = new Date() } = {}) {
  return matchTableRows({ matches: results, ongoings, now })
    .map(row => ({ ...row, match: false, reason: '', chip: null }))
}

/**
 * The action row while the picker is the middle (add 8 → search 4). `draft` is what is typed in the
 * composer right now, `searched` the query a finished search answered, `searching` a search in
 * flight.
 *
 *   · nothing typed, nothing searched → "Search", disabled — and no "Clear selections" (add 8)
 *   · something typed                 → "Clear selections" + "Search" (search 2)
 *   · a search running                → "Clear selections" + the three dots (search 3)
 *   · a search answered, box empty    → "Clear selections" and NO CTA, so the box's own send arrow
 *                                       stands there disabled (search 4)
 *
 * "Clear selections" here clears the SEARCH (`clear: 'search'`) — the frames draw it only once a
 * query exists, so it is about the query, not about the ticked mail the picker is filing.
 */
export function pickerControls({ draft = '', searched = '', searching = false } = {}) {
  const typed = String(draft || '').trim()
  const answered = String(searched || '').trim()
  if (searching) return { clear: 'search', cta: { kind: 'picker-search', label: PICKER_TEXT.search, disabled: true, searching: true } }
  if (typed) return { clear: 'search', cta: { kind: 'picker-search', label: PICKER_TEXT.search, disabled: false, searching: false } }
  if (answered) return { clear: 'search', cta: null }
  return { clear: false, cta: { kind: 'picker-search', label: PICKER_TEXT.search, disabled: true, searching: false } }
}

/** The composer's prompt while the picker is up: the first search, or the next one. */
export function pickerPlaceholder(searched = '') {
  return String(searched || '').trim() ? PICKER_TEXT.continuePlaceholder : PICKER_TEXT.searchPlaceholder
}

/** "8 ongoings found" — the count the frame writes beside the list (40010939:374052). */
export function foundLabel(n) {
  const count = Number(n) || 0
  return `${count} ongoing${count === 1 ? '' : 's'} found`
}

/**
 * The listing's rows: the user's ongoings, filtered by what has been typed, best match first.
 *
 * `ongoingMatches` decides WHICH and in what order (and drops archived stories — filing new mail
 * into something the user has closed is almost never what they meant); this joins its answer back
 * to the record it came from so the row can also carry the stamp and the status dot the frame
 * draws. A story the match list named but the source list no longer holds is dropped rather than
 * drawn with an empty date.
 *
 * `now` is injectable for the same reason `lastUpdate` takes one: a listing of dates must be
 * testable without owning the clock.
 */
export function ongoingPickerRows(ongoings, query = '', now = new Date(), limit = 50) {
  const source = (ongoings || []).filter(o => o && o.id)
  const byId = new Map(source.map(o => [o.id, o]))
  return ongoingMatches(source, query, limit)
    .filter(m => byId.has(m.id))
    .map((m) => {
      const record = byId.get(m.id)
      return pickerRow(m.id, m.title, record, now)
    })
}

/**
 * ONE row, from the story behind it.
 *
 * The frame draws a per-story GLYPH where this listing used to draw a status dot (busy-my-web#523,
 * 40010646:341095: a globe, a tick-circle, a calendar, an envelope, a pencil — one per row). That
 * set is the app's existing per-KIND map, not a new one: `originMeta` already says what shape and
 * what colour an Ongoing's origin is, and it is what the sidebar and the Story header draw. So the
 * row carries the kind's icon and the kind's colour, and keeps the status colour beside them under
 * its old name so nothing that read `colour` has to change.
 */
function pickerRow(id, title, record, now) {
  const status = statusMeta(record.status)
  const kind = originMeta(record.origin)
  return {
    id,
    title,
    updated: lastUpdate(record.updateDate || record.createDate || null, now),
    status: record.status || '',
    statusLabel: status.label,
    colour: status.color,
    icon: kind.icon,
    iconColour: kind.color,
    kindLabel: kind.label,
    // Whether this row is being PRESENTED as a match (see `possibleMatchListRows`). False here:
    // a plain listing row is a search result and nothing more.
    match: false,
  }
}

/**
 * THE "POSSIBLE ONGOING MATCHES" LISTING (busy-my-web#523 + #526, frame 40010646:341095).
 *
 * One list, in two halves, in this order:
 *
 *   1. the real MATCHES — the candidates that cleared lib/possibleMatches' confidence cut-off AND
 *      share evidence with the selection, in the server's own ranking order;
 *   2. everything else the user owns, as SEARCH results — filtered by what has been typed, or the
 *      whole list newest-first when nothing has been.
 *
 * The second half is why the heading can stand with an empty first half (Saffet, #526): the user is
 * never shown a box with nothing in it, and is never shown an unrelated story wearing the word
 * "match" either. `matchCount` is 0 in that case, which is what puts `PICKER_TEXT.noConnection`
 * under the heading.
 *
 * A match the store no longer holds a record for is still drawn, from the candidate's own title,
 * because the ranking naming a story the list has not fetched is a reason to show it rather than a
 * reason to drop the best answer on the page.
 */
export function possibleMatchListRows({
  ongoings = [],
  matches = [],
  query = '',
  now = new Date(),
  limit = 50,
} = {}) {
  const source = (ongoings || []).filter(o => o && o.id)
  const byId = new Map(source.map(o => [String(o.id), o]))
  const q = String(query || '').trim().toLowerCase()
  const seen = new Set()
  const matchRows = []
  for (const m of matches || []) {
    const id = String(m?.id || '')
    if (!id || seen.has(id)) continue
    const title = String(m.title || byId.get(id)?.title || '').trim() || 'Ongoing'
    // A typed query narrows the matches too: the composer is a search over the LIST, and a list
    // that filtered its lower half only would answer a search with rows that do not contain it.
    if (q && !title.toLowerCase().includes(q)) continue
    seen.add(id)
    matchRows.push({ ...pickerRow(id, title, byId.get(id) || {}, now), match: true })
  }
  const rest = ongoingPickerRows(source, query, now, limit).filter(r => !seen.has(String(r.id)))
  return { rows: [...matchRows, ...rest], matchCount: matchRows.length }
}

/**
 * "Linkedin group is connecting to Job-offer ongoing...." (40010928:371958) — and the same sentence
 * without a name behind it, because a picker opened from a selection has no group to name.
 *
 * Shaped as the session's own notice ({ kind, text }) so it rides the banner the Briefing strip
 * already draws for every other thread verb (busy-my-web#274) rather than a second one beside it.
 */
export function connectingNotice(label, ongoingTitle) {
  const who = String(label || '').trim()
  const to = String(ongoingTitle || '').trim()
  const head = who ? `${who} group is connecting` : 'Connecting'
  return { kind: 'running', text: to ? `${head} to ${to} ongoing....` : `${head} to the ongoing....` }
}

/**
 * "Linkedin group connected to Job-offer ongoing" (40010928:372591, spelling corrected above) —
 * GREEN, with the frame's "View" opening the Ongoing (busy-my-web#494 (a)/(b)).
 */
export function connectedNotice(label, ongoingTitle, ongoingId = '') {
  const who = String(label || '').trim()
  const to = String(ongoingTitle || '').trim()
  const id = String(ongoingId || '').trim()
  const head = who ? `${who} group connected` : 'Connected'
  return {
    kind: 'done',
    tone: 'success',
    view: id ? `/ongoing/${id}` : '',
    text: to ? `${head} to ${to} ongoing` : `${head} to the ongoing`,
  }
}

/**
 * What the rail keeps afterwards (40010928:372591's "Linkedin email group · Connected to the
 * ongoing", with the story named under it).
 *
 * The same receipt shape `archiveReceipt` and `bulkReceipt` already produce — the timeline has ONE
 * kind of entry for "something happened to these emails", not one per verb.
 */
export function connectReceipt({ label = '', ongoingTitle = '', count = 0, at = new Date() } = {}) {
  const when = at instanceof Date ? at : new Date(at)
  const who = String(label || '').trim()
  const to = String(ongoingTitle || '').trim()
  const n = Number(count) || 0
  return {
    id: `email-connect:${when.getTime()}`,
    icon: 'sms-tracking',
    title: who ? `${who} email group` : `${n} thread${n === 1 ? '' : 's'}`,
    description: 'Connected to the ongoing',
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when)
      .toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: `${n} thread${n === 1 ? '' : 's'}`,
    // The group's name on its own, so the rail can head itself "<Group> group timeline" while this
    // receipt is its newest subject (frame 40010930:373966, busy-my-web#496).
    label: who,
    rows: to ? [{ id: to, icon: 'ongoing', text: `${to} · ongoing` }] : [],
  }
}

export default ongoingPickerRows
